import { getApiErrorMessage } from 'services/_infra/apiError.utils';
import { getBoundedValue } from 'utils/math/math.utils';
import { displayMarineValue, hasMarineValue } from './marineForecast.utils';

/**
 * Retrieves a list of marine widget locations from the given marine entries.
 * Each location includes an id, label, isPrimaryLocation flag, and sourceIndex.
 * Primary locations are sorted before non-primary locations.
 * Primary locations are determined by whether the city_id is a finite number (i.e., not NaN or Infinity).
 */
export const getMarineWidgetLocations = (marineEntries = []) => {
  const locationsByIdMap = new Map();

  marineEntries.forEach((entry, index) => {
    if (!hasMarineValue(entry?.city_id)) return;

    const locationKey = String(entry.city_id);
    if (locationsByIdMap.has(locationKey)) return;

    const isPrimaryLocation = Number.isFinite(Number.parseInt(entry.city_id, 10));
    const cityName = displayMarineValue(entry?.city_name, 'Unknown marine location');

    locationsByIdMap.set(locationKey, {
      id: entry.city_id,
      label: isPrimaryLocation ? cityName : `Near ${cityName}`,
      isPrimaryLocation,
      sourceIndex: index,
    });
  });

  return Array.from(locationsByIdMap.values()).sort(
    (firstLocation, secondLocation) =>
      Number(secondLocation.isPrimaryLocation) - Number(firstLocation.isPrimaryLocation) ||
      firstLocation.sourceIndex - secondLocation.sourceIndex,
  );
};

/**
 * Retrieves the active marine widget location from the list of locations based on the selected location id.
 */
export const getActiveMarineWidgetLocation = (locations, selectedLocationId) =>
  locations.find(({ id }) => String(id) === String(selectedLocationId)) ?? locations[0] ?? null;

/**
 * Retrieves the marine entries for the specified city id (location).
 */
export const getMarineWidgetLocationEntries = (marineEntries, location) => {
  if (!location) return marineEntries;

  return marineEntries.filter((entry) => String(entry?.city_id) === String(location.id));
};

export const getMarineWidgetLocationOptions = (locations) =>
  locations.map(({ id, label }) => ({ label, value: id }));

export const getActiveMarineWidgetItem = (items, selectedValue, getItemValue = (item) => item) =>
  items.find((item) => String(getItemValue(item)) === String(selectedValue)) ?? items[0] ?? null;

export const getMarineWidgetActiveItemIndex = (items, selectedIndex) =>
  Math.min(selectedIndex, Math.max(items.length - 1, 0));

export const getMarineWidgetErrorMessage = (error, fallbackMessage) =>
  getApiErrorMessage(error, fallbackMessage);

export const getMarineWidgetLayout = ({ containerSizePx, layoutSize, dimensions }) => {
  const columnCount = Math.max(1, Number(dimensions?.[0]) || 1);
  const fallbackWidgetWidth = (layoutSize?.widgetWidth ?? 320) * columnCount;
  const containerWidth = Number.isFinite(containerSizePx?.width)
    ? containerSizePx.width
    : fallbackWidgetWidth;
  const baseFontSize = layoutSize?.baseFontSize ?? 12;
  const arrowButtonWidth = getBoundedValue(
    Math.round(containerWidth * (columnCount === 1 ? 0.11 : 0.065)),
    30,
    50,
  );

  return {
    arrowButtonWidth,
    arrowIconSize: getBoundedValue(Math.round(arrowButtonWidth * 0.42), 14, 20),
    compactVisibleCount: containerWidth <= 340 ? 2 : 3,
    rowTileIconSize: getBoundedValue(Math.round(containerWidth * 0.09), 26, 38),
    tileIconSize: getBoundedValue(Math.round(containerWidth * 0.11), 30, 46),
    tileTextStyle: {
      fontSize: getBoundedValue(Math.min(baseFontSize, Math.round(containerWidth * 0.04)), 11, 14),
      lineHeight: 1.05,
    },
  };
};
